// ════════════════════════════════════════════════════════════════════════════
//  ManDana — undo
//  ---------------------------------------------------------------------------
//  A scoped history of DOCUMENTS. One surface arms a scope, names the keys that
//  scope governs, and from then on everything the user does in it can be taken
//  back — without that surface, or any of its twenty-five call sites, knowing
//  this file exists.
//
//  ── WHY IT WATCHES THE DOOR RATHER THAN THE CALLERS ────────────────────────
//  The obvious way to build an undo is to find every place the app changes
//  something and wrap each one so it records what was there first. That works
//  on the day it is written and starts decaying immediately, because the
//  promise it makes — anything you do can be taken back — is only as good as
//  the next person's memory when they add a mutation and forget the wrapper.
//
//  So nothing is wrapped. md-storage is the one door every document in this app
//  already passes through (its own header says so, and a test enforces it), and
//  `mdOnStorageWrite` reports every change through it with the raw bytes on
//  both sides. A mutation added next month is undoable the day it is written.
//
//  ── A STEP IS A GESTURE, NOT A WRITE ───────────────────────────────────────
//  A React handler runs every write it is going to make synchronously, in one
//  task. So everything seen in one task folds into one step, sealed on a
//  microtask. The hard case then falls out for free: deleting a project writes
//  the index AND removes the project's page; deleting a record writes the day
//  AND its tombstone. Each is one press, because each is one gesture. Wrapping
//  call sites would have given two steps and a ⌘Z that half-undoes.
//
//  Within a step the FIRST value wins per key, so a handler that writes one
//  document twice on its way to an answer undoes to where the gesture began
//  rather than to the halfway house it passed through.
//
//  ── TYPING IS THE EXCEPTION, AND IT BORROWS THE EDITOR'S RULE ──────────────
//  Every keystroke on a page saves, so a history counting saves would give back
//  one letter per press. The editor solved this long ago — a burst ends after
//  MD_HIST_GROUP of quiet — and this reads THE SAME CONSTANT rather than
//  holding a second opinion about what a burst is. Only documents the caller
//  named as `text` may merge, and only into a step that touched that same one
//  document, so a status change landing just after a paragraph stays its own
//  act. Two grains, and they nest: inside the writing ⌘Z is the editor's and
//  moves word by word; step out of it and the paragraph is one press.
//
//  ── ONE STACK PER SURFACE, AND THE FRONTMOST ANSWERS ───────────────────────
//  Not one stack for the app. Every multi-window app scopes undo to the window
//  you are in — macOS per window, Figma per file, VS Code per editor — because
//  a single stack eventually offers to take back something that happened on a
//  surface you have since closed and can no longer see, and an undo you cannot
//  watch land is a surprise rather than an undo.
//
//  A write is offered to each armed scope from the top down and belongs to the
//  first one that governs its key, so an act always lands in the history of the
//  surface it belongs to: a prayer marked while the Projects window is open is
//  the day's to take back, not something sitting in the Projects stack waiting
//  to surprise somebody. The KEYS, separately, are always answered by the top
//  scope alone — see mdHistoryTop and md-history's keydown listener below.
//
//  Closing a scope forgets what it held, which is what the whiteboard does when
//  you leave a board and what Linear does when you navigate away.
//
//  ── AND IT IS WRITTEN DOWN, NOT ONLY HELD ──────────────────────────────────
//  Everything above is memory, and memory ends with the tab. So every sealed
//  step is also a line in the record's log — `history:<record>:<device>`, the
//  key 0.5 chose so that a key has exactly ONE WRITER and last-write-wins is
//  safe by construction rather than by a rule somebody keeps honest. Two
//  devices editing one record while apart is the ordinary case, and both logs
//  have to come back.
//
//  ONE LINE PER GESTURE, for the reason there is one STEP per gesture: deleting
//  a record writes the day document and its tombstone, and a log that said that
//  twice would be describing the implementation rather than the act.
//
//  AND IT CARRIES ONLY WHAT THE RECORD NO LONGER SAYS. What a record says now
//  is what the record says; repeating it would double a SYNCED document for
//  nothing recoverable. So a line names the fields that moved and carries the
//  value each one held — inline while it is small, and otherwise as a pointer
//  to a local version (4.2), which never leaves this device. Without that
//  ceiling "the log is small" stops being true the first time anybody writes an
//  essay, and then two hundred entries of it are pushed to every device.
//
//  ── WHAT IT REFUSES TO RECORD ──────────────────────────────────────────────
//  Documents arriving from another device are written through this very same
//  door. Undoing your phone's edit and pushing the divergence back out is a bug
//  with teeth, so md-sync wraps every remote apply in `mdHistoryQuiet`. The
//  restores this file performs are quiet for the same reason: an undo that
//  recorded itself would immediately push a step undoing the undo.
// ════════════════════════════════════════════════════════════════════════════

/** What one scope was told to govern. A key ending in ':' is a prefix; any
    other key is matched whole — `md:projects` must not claim
    `md:projectViews`, which sits right beside it. */
interface MdHistoryScopeOpts {
  keys: string[];
  /** The subset whose documents are written INTO. Only these may merge into a
      burst; everything else is a deliberate act and earns its own step. */
  text?: string[];
}

/** The bytes a step has to put back, per key. `null` means the document was
    not there, and restoring it means removing it rather than writing an empty
    one in its place. */
type MdHistoryFrame = Map<string, string | null>;

/** What a log line carries about one field: the value the record HELD, inline
    while it is small, or a pointer to the local version holding it. Neither,
    when the field arrived rather than moved — or when the version could not be
    kept, which is a state the reader already has to answer for (md-versions).*/
interface MdHistoryLogged {
  from?: unknown;
  ver?: string;
}

interface MdHistoryStep {
  frame: MdHistoryFrame;
  /** The single key this step touched, when it touched exactly one; null
      otherwise. Only a step with one text key can host a burst. */
  only: string | null;
}

interface MdHistoryScope {
  keys: string[];
  text: string[];
  undo: MdHistoryStep[];
  redo: MdHistoryStep[];
  /** The document a burst is currently extending, and when it last grew. */
  mergeKey: string | null;
  mergeAt: number;
}

type MdHistoryKind = 'push' | 'undo' | 'redo' | 'clear';

(function (g: any) {
  'use strict';

  /* Thirty steps, which is what the database's history keeps. Deep enough that
     nobody reaches the floor by hand, shallow enough that thirty snapshots of
     a page cannot become a memory problem. */
  const MD_HIST_LIMIT = 30;
  /* Half a second of quiet ends a burst — md-markdown.jsx's MD_HIST_GROUP, and
     the same number on purpose: two histories that disagreed about what a
     burst is would be two different answers to one keypress. */
  const MD_HIST_GROUP = 500;

  const scopes = new Map<string, MdHistoryScope>();
  /* Arming order. The last one armed is the surface in front of you. */
  const order: string[] = [];

  /* Depth rather than a flag: quiet nests, and the sync layer wrapping an
     apply that itself writes through another quiet region must not un-quiet
     the outer one on the way back out. */
  let quiet = 0;

  /* The gesture in flight. It holds ONE scope: a write belonging to a different
     scope seals what is pending before opening its own, so a step can never
     span two stacks. */
  let pending: { scope: string; frame: MdHistoryFrame; only: string | null } | null = null;
  let sealQueued = false;

  function scopeOf(name: unknown): MdHistoryScope | null {
    return typeof name === 'string' ? scopes.get(name) || null : null;
  }

  function governs(scope: MdHistoryScope, key: string): boolean {
    for (const pattern of scope.keys) {
      if (pattern.endsWith(':') ? key.startsWith(pattern) : key === pattern) return true;
    }
    return false;
  }

  function isText(scope: MdHistoryScope, key: string): boolean {
    for (const pattern of scope.text) {
      if (pattern.endsWith(':') ? key.startsWith(pattern) : key === pattern) return true;
    }
    return false;
  }

  /** Which scope owns a key: the frontmost one that governs it. */
  function ownerOf(key: string): string | null {
    for (let i = order.length - 1; i >= 0; i--) {
      const name = order[i] as string;
      const scope = scopes.get(name);
      if (scope && governs(scope, key)) return name;
    }
    return null;
  }

  function stateOf(scope: MdHistoryScope | null): { canUndo: boolean; canRedo: boolean } {
    if (!scope) return { canUndo: false, canRedo: false };
    return { canUndo: scope.undo.length > 0, canRedo: scope.redo.length > 0 };
  }

  function announce(name: string, kind: MdHistoryKind): void {
    if (typeof g.dispatchEvent !== 'function' || typeof g.CustomEvent !== 'function') return;
    g.dispatchEvent(new g.CustomEvent('mdhistorychange', {
      detail: { scope: name, kind, ...stateOf(scopes.get(name) || null) },
    }));
  }

  /* ── recording ───────────────────────────────────────────────────────────
     Everything below runs inside md-storage's watcher, which is to say inside
     somebody else's write. It must do as little as possible and it must never
     throw: a history that broke a save would be worse than no history. */

  function sealNow(): void {
    sealQueued = false;
    const step = pending;
    pending = null;
    if (!step) return;
    const scope = scopes.get(step.scope);
    if (!scope) return;                       // closed while the gesture was in flight

    const at = Date.now();
    /* A burst extends the step below rather than adding one: the step already
       there holds the bytes from where the burst BEGAN, which is where an undo
       should land. Nothing is added to it — the later `before` values are
       states the writer typed through, not states to return to. */
    const mergeable = step.only !== null
      && isText(scope, step.only)
      && scope.mergeKey === step.only
      && at - scope.mergeAt < MD_HIST_GROUP
      && scope.undo.length > 0;
    if (mergeable) {
      scope.mergeAt = at;
      return;
    }

    scope.undo.push({ frame: step.frame, only: step.only });
    if (scope.undo.length > MD_HIST_LIMIT) {
      scope.undo.splice(0, scope.undo.length - MD_HIST_LIMIT);
    }
    scope.redo = [];
    scope.mergeKey = step.only !== null && isText(scope, step.only) ? step.only : null;
    scope.mergeAt = at;
    /* Written where the step is CREATED and nowhere else, so a burst extends
       what the log already says about it exactly as it extends the step —
       one line per paragraph, pointing at what the record said before it.

       Never allowed to throw, for the reason stated above this section: a
       history that broke a save would be worse than no history. */
    try { logStep(step.frame, at); } catch (e) { /* not the writer's problem */ }
    announce(step.scope, 'push');
  }

  function record(key: string, before: string | null): void {
    if (quiet) return;
    const name = ownerOf(key);
    if (!name) return;
    // A gesture belongs to one scope. A write for another seals this one first.
    if (pending && pending.scope !== name) sealNow();
    if (!pending) pending = { scope: name, frame: new Map(), only: key };
    // First value wins: undo returns to where the gesture began.
    if (!pending.frame.has(key)) pending.frame.set(key, before);
    if (pending.only !== key) pending.only = null;
    if (!sealQueued) { sealQueued = true; queueMicrotask(sealNow); }
  }

  if (typeof g.mdOnStorageWrite === 'function') {
    g.mdOnStorageWrite((change: { key: string; before: string | null }) => {
      record(change.key, change.before);
    });
  }

  /* ── and written down ─────────────────────────────────────────────────────
     A step is bytes per KEY, which is the right currency for putting a document
     back and the wrong one for saying what happened: nobody wants to be told
     that `md:records:2026-08-29` moved. So md-versions.ts reads the records out
     of a document — it owns that reading because both halves of this phase need
     it — and what is written here is the difference between two of those.

     THE LINE IS QUIET INSIDE ITSELF, and that is not tidiness: the log goes
     through the very door this module watches, so a scope governing a prefix
     that happened to cover `md:history:` would record its own record of a
     gesture, and then record that. */

  /* Bookkeeping rather than writing. `updatedAt` moves on every edit, so a line
     naming it would name it every time and say nothing; the rest are identity
     or a clock. md-merge.ts's contentKey skips the same stamp for the same
     reason, and this is that list plus the fields a ref is made of. */
  const NOT_A_FIELD = new Set(['uid', 'type', 'created', 'updatedAt', 'at']);

  function fieldsOf(row: Record<string, unknown> | undefined): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if (!row) return out;
    for (const key of Object.keys(row)) if (!NOT_A_FIELD.has(key)) out[key] = row[key];
    return out;
  }

  /* Compared as bytes, because a field can hold a list — a project's links, a
     record's tags — and two of those are the same when they read the same. An
     absent field serialises to '', which no present value can. */
  function json(value: unknown): string {
    try { return JSON.stringify(value) ?? ''; } catch (e) { return ''; }
  }

  function parsed(raw: string | null): unknown {
    if (raw == null) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  /** Keep what the record USED TO SAY, and answer where it went. */
  function keptVersion(ref: string, was: Record<string, unknown>, at: number): string {
    if (typeof g.mdRecordDocText !== 'function' || typeof g.mdVersionKeep !== 'function') return '';
    const text = String(g.mdRecordDocText(was) || '');
    return text ? String(g.mdVersionKeep(ref, text, { at, what: 'text' }) || '') : '';
  }

  /** One record's line, or null when nothing about it moved. */
  function lineFor(ref: string, was: Record<string, unknown> | undefined,
    now: Record<string, unknown> | undefined, at: number, inline: number): MdHistoryLogEntry | null {
    /* A record that has just arrived carries nothing: what it says is what it
       says, and the log's whole economy is that it never repeats that. */
    if (!was) return now ? { at, what: 'add' } : null;

    const before = fieldsOf(was);
    const after = fieldsOf(now);
    const moved = [...new Set([...Object.keys(before), ...Object.keys(after)])]
      .filter((f) => json(before[f]) !== json(after[f]))
      .sort();
    if (!moved.length) return null;

    const fields: Record<string, MdHistoryLogged> = {};
    let ver: string | null = null;
    for (const field of moved) {
      const held = json(before[field]);
      // A field that ARRIVED has nothing to carry, and the line still names it.
      if (!held) { fields[field] = {}; continue; }
      if (held.length <= inline) { fields[field] = { from: before[field] }; continue; }
      /* ONE VERSION PER GESTURE, not one per long field. It holds what the
         RECORD said, so every long field on this line points at the same row —
         which is what keeps a version list a list of what a record used to say
         rather than a pile of fragments nobody can put back together. */
      if (ver === null) ver = keptVersion(ref, was, at);
      fields[field] = ver ? { ver } : {};
    }
    return { at, what: now ? 'edit' : 'remove', fields };
  }

  function logStep(frame: MdHistoryFrame, at: number): void {
    const isHome = g.mdRecordDocIsHome;
    const rows = g.mdRecordDocRows;
    const append = g.mdHistoryLogAppend;
    const inline = Number(g.MD_HISTORY_INLINE_MAX);
    /* md-versions.ts loads after this file and is absent in a headless run. No
       store, no log — and the stack above goes on working either way. */
    if (typeof isHome !== 'function' || typeof rows !== 'function'
      || typeof append !== 'function' || !Number.isFinite(inline)) return;

    /* THE WHOLE FRAME BEFORE ANYTHING IS COMPARED, which is the same argument
       as "a step is a gesture" one floor down. Rescheduling a record takes it
       out of one day's document and puts it in another's, in one handler.
       Compared key by key that reads as a deletion followed by an unrelated
       arrival — a lie about the record, and the worst kind, because the log is
       what somebody consults when they cannot find something. Unioned first it
       is what it actually was: one record, one line, naming the day it moved
       to. Nothing here needs to know that a reschedule is even possible. */
    const was: Record<string, Record<string, unknown>> = {};
    const now: Record<string, Record<string, unknown>> = {};
    for (const [key, before] of frame) {
      /* The shape is asked before either side is parsed. A gesture on a
         whiteboard must not cost a JSON.parse of the drawing to discover there
         was never a record in it. */
      if (!isHome(key)) continue;
      Object.assign(was, rows(key, parsed(before)));
      Object.assign(now, rows(key, parsed(currentOf(key))));
    }

    const lines: [string, MdHistoryLogEntry][] = [];
    for (const ref of new Set([...Object.keys(was), ...Object.keys(now)])) {
      const line = lineFor(ref, was[ref], now[ref], at, inline);
      if (line) lines.push([ref, line]);
    }
    if (!lines.length) return;

    quiet += 1;
    try {
      for (const [ref, line] of lines) append(ref, line);
    } finally {
      quiet -= 1;
    }
  }

  /* ── restoring ───────────────────────────────────────────────────────────
     Raw bytes back, so a restored document is the one that was there rather
     than a re-serialised guess at it. */

  function currentOf(key: string): string | null {
    return typeof g.mdReadRaw === 'function' ? g.mdReadRaw(key) : null;
  }

  function put(key: string, raw: string | null): boolean {
    return raw === null ? !!g.mdRemove(key) : !!g.mdWriteRaw(key, raw);
  }

  /** Restore one frame, returning the frame that undoes it — or null if the
      device refused, in which case everything already written is put back and
      the caller's stacks are left exactly as they were. */
  function apply(frame: MdHistoryFrame): MdHistoryFrame | null {
    const inverse: MdHistoryFrame = new Map();
    for (const key of frame.keys()) inverse.set(key, currentOf(key));

    const done: string[] = [];
    let ok = true;
    quiet += 1;
    try {
      for (const [key, raw] of frame) {
        if (!put(key, raw)) { ok = false; break; }
        done.push(key);
      }
      if (!ok) {
        // A half-applied undo is a document nobody can get back. Walk it out.
        for (const key of done) put(key, inverse.get(key) ?? null);
      }
    } finally {
      quiet -= 1;
    }
    return ok ? inverse : null;
  }

  function step(name: unknown, from: 'undo' | 'redo', kind: MdHistoryKind): boolean {
    const scope = scopeOf(name);
    if (!scope) return false;
    const stack = from === 'undo' ? scope.undo : scope.redo;
    const top = stack[stack.length - 1];
    if (!top) return false;

    /* Whatever was still in flight belongs BELOW this step, not folded into
       it — otherwise an undo pressed mid-burst takes back the burst and the
       step beneath in one press. */
    if (pending && pending.scope === name) sealNow();

    const inverse = apply(top.frame);
    if (!inverse) return false;               // refused; the stack is untouched

    stack.pop();
    const other = from === 'undo' ? scope.redo : scope.undo;
    other.push({ frame: inverse, only: top.only });
    /* The burst this might have extended is one the writer has just stepped
       out of, so the next keystroke starts a fresh step rather than merging
       into a state nobody is standing in. */
    scope.mergeKey = null;
    scope.mergeAt = 0;
    announce(name as string, kind);
    return true;
  }

  /* ── the doors ───────────────────────────────────────────────────────────*/

  /** Arm a scope. Arming one that is already armed changes nothing — a surface
      that re-runs its effect must not end up stacked above itself. */
  function mdHistoryOpen(name: unknown, opts: MdHistoryScopeOpts): boolean {
    if (typeof name !== 'string' || !name) return false;
    if (scopes.has(name)) return true;
    const keys = Array.isArray(opts && opts.keys) ? opts.keys.filter((k) => typeof k === 'string') : [];
    if (!keys.length) return false;
    const asked = opts && opts.text;
    const text = Array.isArray(asked) ? asked.filter((k) => typeof k === 'string') : [];
    scopes.set(name, { keys, text, undo: [], redo: [], mergeKey: null, mergeAt: 0 });
    order.push(name);
    return true;
  }

  /** Disarm a scope and forget what it held, including a gesture still in
      flight — a step sealed after its surface has gone is one nobody can watch
      land. */
  function mdHistoryClose(name: unknown): boolean {
    if (typeof name !== 'string' || !scopes.has(name)) return false;
    if (pending && pending.scope === name) pending = null;
    scopes.delete(name);
    const at = order.indexOf(name);
    if (at !== -1) order.splice(at, 1);
    announce(name, 'clear');
    return true;
  }

  /** The surface in front of you: the scope armed last. */
  function mdHistoryTop(): string | null {
    return order.length ? (order[order.length - 1] as string) : null;
  }

  function mdHistoryState(name: unknown): { canUndo: boolean; canRedo: boolean } {
    return stateOf(scopeOf(name));
  }

  function mdHistoryUndo(name: unknown): boolean { return step(name, 'undo', 'undo'); }
  function mdHistoryRedo(name: unknown): boolean { return step(name, 'redo', 'redo'); }

  /** Seal the gesture in flight now. A surface calls this when the thing being
      written to goes away — leaving a project's page must end that page's
      burst, or the next page's first keystroke joins it. */
  function mdHistorySeal(name: unknown): void {
    const scope = scopeOf(name);
    if (!scope) return;
    if (pending && pending.scope === name) sealNow();
    scope.mergeKey = null;
    scope.mergeAt = 0;
  }

  /** Run work with recording suspended. Nests, and un-suspends even if the work
      throws — a quiet that leaked would silently turn the undo off for the rest
      of the session.

      Asynchronous work is held to the same promise. md-sync's mdApplyDoc is an
      `async` function, and a naive `finally` would drop the counter the instant
      the work reached its first `await` — everything it wrote past that point
      would be recorded, and the hole would open silently in whichever pull
      route grew an await first. So a thenable extends the quiet until it has
      actually settled, and the caller gets a promise that resolves after it. */
  function mdHistoryQuiet<T>(work: () => T): T {
    quiet += 1;
    let out: T;
    try {
      out = work();
    } catch (err) {
      quiet -= 1;
      throw err;
    }
    const later = out as unknown as { then?: unknown } | null;
    if (!later || typeof later.then !== 'function') {
      quiet -= 1;
      return out;
    }
    return (out as unknown as Promise<unknown>).then(
      (value) => { quiet -= 1; return value; },
      (err) => { quiet -= 1; throw err; },
    ) as unknown as T;
  }

  /* ── the keys ────────────────────────────────────────────────────────────

     ⌘Z already means three different things in this app, and all three are
     right: in your writing it walks the editor's burst ring, in a database the
     database's stack, on the whiteboard Excalidraw's. Adding a fourth meaning
     is only safe if the fourth one is the LAST to speak — so this listener sits
     on window, in the bubble phase, and declines the press on three separate
     readings, any one of which is enough on its own.

     None of the three surfaces that already own ⌘Z change at all. That is the
     point of reading `defaultPrevented`: each of them already claims the press,
     so each of them already says no to this. The attribute covers the rest —
     the paths where the browser answers the key rather than the surface — and
     the caret rule is the focus-decides convention every app converges on. */

  const MD_CARET = 'input, textarea, select, [contenteditable="true"]';

  /** Which of our two keys this is, if either. Command or control for undo,
      shift for redo, and control+Y as well because that is the other hand this
      app already answers in a database. */
  function asks(event: KeyboardEvent): 'undo' | 'redo' | null {
    if (!event.metaKey && !event.ctrlKey) return null;
    if (event.altKey) return null;
    const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
    if (key === 'z') return event.shiftKey ? 'redo' : 'undo';
    if (key === 'y' && event.ctrlKey && !event.metaKey && !event.shiftKey) return 'redo';
    return null;
  }

  function heard(event: KeyboardEvent): void {
    const want = asks(event);
    if (!want) return;
    // 1 · somebody already answered it.
    if (event.defaultPrevented) return;
    const target = event.target as { closest?: (sel: string) => unknown } | null;
    if (target && typeof target.closest === 'function') {
      // 2 · a surface that owns its undo.  3 · a caret.
      if (target.closest('[data-md-own-undo]')) return;
      if (target.closest(MD_CARET)) return;
    }
    /* With nothing armed the key is not ours, and the browser's own undo is
       left alone. With a surface armed it IS ours, whether or not there is
       anything left to take back — an empty stack that let the press through
       would revert some field elsewhere on the page. */
    const name = mdHistoryTop();
    if (!name) return;
    event.preventDefault();
    if (want === 'undo') mdHistoryUndo(name);
    else mdHistoryRedo(name);
  }

  if (typeof g.addEventListener === 'function') g.addEventListener('keydown', heard);

  Object.assign(g, {
    mdHistoryOpen, mdHistoryClose, mdHistoryTop, mdHistoryState,
    mdHistoryUndo, mdHistoryRedo, mdHistorySeal, mdHistoryQuiet,
    MD_HIST_LIMIT, MD_HIST_GROUP,
  });
})(typeof window !== 'undefined' ? window : globalThis);
